import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { supabase } from "@/integrations/supabase/client";
import { Toaster } from "@/components/ui/sonner";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { CookieBanner } from "@/components/CookieBanner";
import { TosReacceptGate } from "@/components/TosReacceptGate";

import { CommandPalette } from "@/components/CommandPalette";
import { AuthLoadingOverlay } from "@/components/AuthLoadingOverlay";
import { registerServiceWorker } from "@/lib/register-sw";
import { BRAND } from "@/lib/brand";
import { initSentry } from "@/lib/sentry";

if (typeof window !== "undefined") {
  initSentry();
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="tracking-wide-luxury text-accent">The Hospitality Reserve</p>
        <h1 className="mt-6 font-display text-6xl text-foreground">404</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          This corridor is not part of the establishment.
        </p>
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center justify-center border border-foreground px-6 py-3 text-xs tracking-wide-luxury text-foreground transition-colors hover:bg-foreground hover:text-background"
          >
            Return to the Lobby
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="tracking-wide-luxury text-accent">An Interruption</p>
        <h1 className="mt-4 font-display text-3xl text-foreground">
          Our apologies — this page is momentarily unavailable.
        </h1>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="border border-foreground px-6 py-3 text-xs tracking-wide-luxury hover:bg-foreground hover:text-background transition-colors"
          >
            Try Again
          </button>
          <a
            href="/"
            className="border border-border px-6 py-3 text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground transition-colors"
          >
            Return Home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: `${BRAND.name} — ${BRAND.tagline}` },
      { name: "description", content: BRAND.description },
      { name: "application-name", content: BRAND.name },
      { name: "apple-mobile-web-app-title", content: BRAND.name },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "theme-color", content: "#2E6B8A" },
      { name: "msapplication-TileColor", content: "#FFFFFF" },
      { property: "og:site_name", content: BRAND.name },
      { property: "og:title", content: BRAND.name },
      { property: "og:description", content: BRAND.ogDescription },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: BRAND.name },
      { name: "twitter:description", content: BRAND.ogDescription },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/62d5bc67-02ee-424d-954e-017349d68978/id-preview-752ddfe3--0aaef45b-1ffc-4f4a-b152-bc6bd32e283d.lovable.app-1782133527852.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/62d5bc67-02ee-424d-954e-017349d68978/id-preview-752ddfe3--0aaef45b-1ffc-4f4a-b152-bc6bd32e283d.lovable.app-1782133527852.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "" },
      // Preload the font CSS so the browser fetches it at highest priority
      // alongside our app CSS. `display=swap` prevents FOIT.
      {
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: BRAND.name,
              url: "https://thehospitalityreserve.lovable.app",
              description: BRAND.description,
              areaServed: BRAND.location,
              email: BRAND.supportEmail,
            },
            {
              "@type": "WebSite",
              name: BRAND.name,
              url: "https://thehospitalityreserve.lovable.app",
              description: BRAND.ogDescription,
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <style dangerouslySetInnerHTML={{ __html: `html, body { background-color: #FFFFFF; }` }} />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}


function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    let cancelled = false;

    // Global suspension fallback: regardless of which route the user is on
    // (including public/marketing pages and /auth), if the active session
    // belongs to a suspended account, sign them out and bounce to the
    // dedicated /account-suspended screen with the reason pre-filled. The
    // per-route _authenticated gate already blocks protected pages; this is
    // the safety net for deep links into anything else.
    const checkSuspended = async () => {
      try {
        const { data: sessionRes } = await supabase.auth.getSession();
        const user = sessionRes.session?.user;
        if (!user || cancelled) return;
        const { data: prof } = await supabase
          .from("profiles")
          .select("is_suspended, suspension_reason")
          .eq("id", user.id)
          .maybeSingle();
        if (cancelled) return;
        const row = prof as { is_suspended: boolean | null; suspension_reason: string | null } | null;
        if (row?.is_suspended) {
          const reason = (row.suspension_reason ?? "Your account has been suspended.").slice(0, 240);
          try { await supabase.auth.signOut(); } catch { /* ignore */ }
          if (window.location.pathname !== "/account-suspended") {
            window.location.replace(
              `/account-suspended?reason=${encodeURIComponent(reason)}`,
            );
          }
        }
      } catch {
        /* ignore — non-fatal */
      }
    };

    // Run once on mount to catch deep links.
    void checkSuspended();

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      // Only act on sign-out (kick to /auth) and user-updated (refresh data).
      // Do NOT invalidate on SIGNED_IN — the sign-in page navigates itself,
      // and a concurrent invalidate races the destination route's getUser()
      // call and aborts it in Safari ("Load failed" → bounce back to /auth).
      if (event === "SIGNED_IN" || event === "USER_UPDATED" || event === "TOKEN_REFRESHED") {
        // Recheck suspension on any identity change.
        void checkSuspended();
      }
      if (event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [router, queryClient]);

  useEffect(() => {
    // Production-only, guarded against Lovable preview hosts and iframes.
    void registerServiceWorker();
  }, []);

  useEffect(() => {
    // Per-route load timing — logs "[perf] route ... rendered in Xms" on
    // every navigation. Filter DevTools console by "[perf]" to inspect.
    let startedAt = (typeof performance !== "undefined" ? performance.now() : Date.now());
    let pendingPath = router.state.location.pathname;
    const unsub = router.subscribe("onBeforeNavigate", (evt) => {
      startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
      pendingPath = evt.toLocation.pathname;
    });
    const unsubResolved = router.subscribe("onResolved", () => {
      const ms = Math.round(
        (typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt,
      );
      // eslint-disable-next-line no-console
      console.info(`[perf] route "${pendingPath}" rendered in ${ms}ms`);
    });
    return () => { unsub(); unsubResolved(); };
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <PaymentTestModeBanner />
      <Outlet />
      <CommandPalette />
      <CookieBanner />
      <TosReacceptGate />

      <Toaster position="top-center" richColors={false} closeButton expand={false} visibleToasts={3} />
      <AuthLoadingOverlay />
    </QueryClientProvider>
  );
}
